import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { Button, Grid, TextField, MenuItem, Stack } from '@mui/material'
import { CatalogProductType } from '../enums/CatalogProductType'
import { CatalogProductInput } from '../types'
import { createCatalogProduct, updateCatalogProduct } from '../redux/slices/catalog'
import catalogProductService from '../services/catalogProductService'
import { NominalCurrentSelectForSmartElectricalCabinet } from './NominalCurrentSelect'
import PowerSupplyInputCablesSelect from './PowerSupplyInputCablesSelect'
import { CatalogProduct } from '../types'
import {
  isRequired,
  mustBeBoolean,
  mustBeNumber,
  mustBeString,
  mustBeStringOrNull
} from '../utils/validation'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import HeatingPowerAutocomplete from './HeatingPowerAutocomplete'
import { CatalogProductCustomDataInput } from '../types/CatalogProductInput'
import { LoadingButton } from '@mui/lab'
import { usePolicy } from '../hooks'
import { CatalogProductStatus } from '../enums'

interface CatalogProductFormProps {
  type?: CatalogProductType
  catalogProduct?: CatalogProduct
  onCancel?: () => void
  beforeSubmit?: (values: CatalogProductInput, helpers: FormikHelpers<CatalogProductInput>) => void
  afterSubmit?: (
    catalogProduct: CatalogProduct,
    helpers: FormikHelpers<CatalogProductInput>
  ) => void
}

export default function CatalogProductForm({
  type,
  catalogProduct,
  onCancel,
  beforeSubmit,
  afterSubmit
}: CatalogProductFormProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy state.
   */
  const { isSuperadmin, isManufacturer } = usePolicy()

  const initialValues: CatalogProductInput = {
    type: catalogProduct?.type ?? type ?? CatalogProductType.ELECTRICAL_CABINET,
    name: catalogProduct?.name ?? '',
    description: catalogProduct?.description ?? '',
    manufacturer: catalogProduct?.manufacturer ?? '',
    manufacturerProductCode: catalogProduct?.manufacturerProductCode ?? '',
    status:
      isSuperadmin || isManufacturer
        ? CatalogProductStatus.VERIFIED
        : CatalogProductStatus.UNVERIFIED,
    customData: getInitialCustomData()
  }

  /**
   * The validation schema
   */
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .typeError(mustBeString(t('name')))
      .required(isRequired(t('name'))),
    type: Yup.string()
      .typeError(mustBeString(t('type')))
      .required(isRequired(t('type'))),
    manufacturer: Yup.string()
      .typeError(mustBeString(t('manufacturer')))
      .required(isRequired(t('manufacturer'))),
    manufacturerProductCode: Yup.string()
      .typeError(mustBeStringOrNull(t('manufacturerProductCode')))
      .required(isRequired(t('manufacturerProductCode')))
      .nullable(),
    customData: Yup.object().shape({
      fuse: Yup.number().when('type', {
        is: (type: CatalogProductType) =>
          type === CatalogProductType.ELECTRICAL_CABINET ||
          type === CatalogProductType.SPINE_MODULE,
        then: Yup.number()
          .typeError(mustBeNumber(t('fuse')))
          .required(isRequired(t('fuse')))
      }),
      inputCables: Yup.number().when('type', {
        is: (type: CatalogProductType) =>
          type === CatalogProductType.ELECTRICAL_CABINET ||
          type === CatalogProductType.SPINE_MODULE,
        then: Yup.number()
          .typeError(mustBeNumber(t('inputCables')))
          .required(isRequired(t('inputCables')))
      }),
      isMainDistributionAssembly: Yup.boolean().when('type', {
        is: (type: CatalogProductType) =>
          type === CatalogProductType.ELECTRICAL_CABINET ||
          type === CatalogProductType.SPINE_MODULE,
        then: Yup.boolean()
          .typeError(mustBeBoolean(t('isMainDistributionAssembly')))
          .required(isRequired(t('isMainDistributionAssembly')))
      }),
      heatingPower: Yup.number().when('type', {
        is: (type: CatalogProductType) => type === CatalogProductType.HEATER,
        then: Yup.number()
          .typeError(mustBeNumber(t('heatingPower')))
          .required(isRequired(t('heatingPower')))
      })
    })
  })

  function getInitialCustomData(): CatalogProductCustomDataInput {
    if (catalogProduct !== undefined) {
      return catalogProduct.customData ?? {}
    } else if (type === CatalogProductType.HEATER) {
      return { heatingPower: 0 }
    } else {
      return {
        fuse: 0,
        isMainDistributionAssembly: false,
        inputCables: 0
      }
    }
  }

  async function handleSubmit(
    values: CatalogProductInput,
    { setSubmitting, ...helpers }: FormikHelpers<CatalogProductInput>
  ): Promise<void> {
    try {
      setSubmitting(true)

      beforeSubmit?.(values, { setSubmitting, ...helpers })

      if (catalogProduct === undefined) {
        const createdCatalogProduct = await catalogProductService.createCatalogProduct(values)

        dispatch(createCatalogProduct(createdCatalogProduct))
        dispatch(createSuccessNotification(t('catalogProductCreateSuccess')))

        afterSubmit?.(createdCatalogProduct, { setSubmitting, ...helpers })
      } else {
        const updatedCatalogProduct = await catalogProductService.updateCatalogProduct(
          catalogProduct.id,
          values
        )

        dispatch(updateCatalogProduct(updatedCatalogProduct))
        dispatch(createSuccessNotification(t('catalogProductUpdateSuccess')))

        afterSubmit?.(updatedCatalogProduct, { setSubmitting, ...helpers })
      }
    } catch (_) {
      if (catalogProduct === undefined) {
        dispatch(createErrorNotification(t('createCatalogProductError')))
      } else {
        dispatch(createErrorNotification(t('updateCatalogProductError')))
      }
    } finally {
      setSubmitting(false)
    }
  }

  function handleCancel(): void {
    onCancel?.()
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleBlur,
        handleChange,
        handleSubmit
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={6}>
            {type === undefined && (
              <Grid item xs={12}>
                <TextField
                  select
                  name="type"
                  label={t('type')}
                  value={values.type}
                  error={Boolean(touched.type && errors.type)}
                  helperText={touched.type && errors.type}
                  variant="outlined"
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                >
                  <MenuItem value={CatalogProductType.ELECTRICAL_CABINET}>
                    {t('electricalCabinet')}
                  </MenuItem>
                  <MenuItem value={CatalogProductType.SPINE_MODULE}>{t('spineModule')}</MenuItem>
                  <MenuItem value={CatalogProductType.HEATER}>{t('heater')}</MenuItem>
                </TextField>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                name="name"
                label={t('name')}
                value={values.name}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                variant="outlined"
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="manufacturer"
                label={t('manufacturer')}
                value={values.manufacturer}
                error={Boolean(touched.manufacturer && errors.manufacturer)}
                helperText={touched.manufacturer && errors.manufacturer}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="manufacturerProductCode"
                label={t('manufacturerProductCode')}
                value={values.manufacturerProductCode}
                error={Boolean(touched.manufacturerProductCode && errors.manufacturerProductCode)}
                helperText={touched.manufacturerProductCode && errors.manufacturerProductCode}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            {(values.type === CatalogProductType.ELECTRICAL_CABINET ||
              values.type === CatalogProductType.SPINE_MODULE) && (
              <>
                <Grid item xs={12} md={3}>
                  <NominalCurrentSelectForSmartElectricalCabinet
                    name="customData.fuse"
                    value={values.customData.fuse}
                    error={Boolean(touched?.customData?.fuse && errors?.customData?.fuse)}
                    helperText={touched?.customData?.fuse && errors?.customData?.fuse}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <PowerSupplyInputCablesSelect
                    name="customData.inputCables"
                    label={t('PowerSupply.inputCables')}
                    value={values.customData.inputCables}
                    error={Boolean(
                      touched?.customData?.inputCables && errors?.customData?.inputCables
                    )}
                    helperText={touched?.customData?.inputCables && errors?.customData?.inputCables}
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    name="customData.isMainDistributionAssembly"
                    label={t('isMainDistributionAssembly')}
                    value={values.customData.isMainDistributionAssembly}
                    error={Boolean(
                      touched?.customData?.isMainDistributionAssembly &&
                      errors?.customData?.isMainDistributionAssembly
                    )}
                    helperText={
                      touched?.customData?.isMainDistributionAssembly &&
                      errors?.customData?.isMainDistributionAssembly
                    }
                    variant="outlined"
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                  >
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    <MenuItem value={false as any}>{t('no')}</MenuItem>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    <MenuItem value={true as any}>{t('yes')}</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            {values.type === CatalogProductType.HEATER && (
              <Grid item xs={12}>
                <HeatingPowerAutocomplete
                  name="customData.heatingPower"
                  label={t('heater:heatingPower')}
                  value={values.customData.heatingPower}
                  error={Boolean(
                    touched?.customData?.heatingPower && errors?.customData?.heatingPower
                  )}
                  helperText={touched?.customData?.heatingPower && errors?.customData?.heatingPower}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={(value) => {
                    setFieldValue('customData.heatingPower', value)
                    setFieldTouched('customData.heatingPower', true)
                  }}
                />
              </Grid>
            )}

            {(isSuperadmin || isManufacturer) && (
              <Grid item xs={12}>
                <TextField
                  select
                  name="status"
                  label={t('status')}
                  value={values.status}
                  error={Boolean(touched.status && errors.status)}
                  helperText={touched.status && errors.status}
                  variant="outlined"
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                >
                  <MenuItem value={CatalogProductStatus.UNVERIFIED}>
                    {t('manufacturing:CatalogProductStatus.unverified')}
                  </MenuItem>
                  <MenuItem value={CatalogProductStatus.VERIFIED}>
                    {t('manufacturing:CatalogProductStatus.verified')}
                  </MenuItem>
                </TextField>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                name="description"
                label={t('description')}
                value={values.description}
                error={Boolean(touched.description && errors.description)}
                helperText={touched.description && errors.description}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={3}>
                <Button variant="text" color="inherit" onClick={handleCancel}>
                  {t('cancel')}
                </Button>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                >
                  {t('save')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  )
}
